import { Alert, Box, Button, Card, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OrderInfo } from "../../shared/orderInfo/types";
import OrderDetails from "../OrderDetails";
import { AuthorizationStatus, FundingPackage, FundingPackageLockMode, FundingPackageStatus } from "../types";
import { useDropzone } from "react-dropzone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReviewFundingPackageDetails from "./ReviewFundingPackageDetails";
import ReviewUploadFunding from "./ReviewUploadFunding";
import { FundingAuthorizationContext } from "../FundingAuthorizationContext";
import { UserContext } from "../../contexts/UserContext";
import NotesSection from "../NotesSection";
import WiredIFrame from "../WiredIframe";
import { useFundingPackageLock } from "../useFundingPackageLock";
import FundingPackageLockBanner from "../FundingPackageLockBanner";

export default function ReviewFundingPackage() {
  const { user } = useContext(UserContext);
  const { compositeId } = useParams<{ compositeId: string }>();
  const [orderNumber, rawPackageNumber] = compositeId?.split("-pkg") ?? [];
  const packageNumber = Number(rawPackageNumber ?? 1);
  const navigate = useNavigate();
  const { fetchFundingPackages } = useContext(FundingAuthorizationContext);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [fundingPackage, setFundingPackage] = useState<FundingPackage>();
  const [statusUpdateError, setStatusUpdateError] = useState("");
  const packageLock = useFundingPackageLock(fundingPackage?.id, FundingPackageLockMode.Review);
  const isWireApprover = user?.internalUser?.internalUserRoles?.find(
    (iu) => iu.internalUserRoleType?.name === "Wire Approver"
  );

  useEffect(() => {
    if (user != null && !isWireApprover) {
      navigate("/unauthorized");
    }
  }, [user, isWireApprover]);
  const getPackageInfo = async () => {
    const { data } = await axios.get(
      `/api/FundingPackages/packages/GetPackageByOrderAndPackageNumber?orderNumber=${orderNumber}&packageNumber=${packageNumber}`
    );
    setFundingPackage(data);
    if (data.orderNumber) {
      getAndSetOrderInfo(data.orderNumber);
    }
  };
  const getAndSetOrderInfo = async (orderNumber: string) => {
    try {
      const { data } = await axios.get(`/proxy/api/orders/getOrderInfo?orderNumber=${orderNumber}`);
      setOrderInfo(data);
    } catch (error) {
      console.error("Failed to fetch order info:", error);
    }
  };

  const submitDeficient = async () => {
    setStatusUpdateError("");
    if (!(await packageLock.ensureOwnership())) {
      setStatusUpdateError("This package is no longer locked to you. Refresh the lock or request control again.");
      return;
    }

    try {
      await axios.post("/api/FundingPackages/packages/UpdateFundingPackageStatus", {
        fundingPackageId: fundingPackage?.id,
        status: FundingPackageStatus.Deficient
      });
      await fetchFundingPackages();
      navigate("/funding-packages");
    } catch (error) {
      setStatusUpdateError("This package is no longer locked to you. Refresh the lock or request control again.");
      await packageLock.refreshLock();
    }
  };
  const submitFunded = async () => {
    setStatusUpdateError("");
    if (!(await packageLock.ensureOwnership())) {
      setStatusUpdateError("This package is no longer locked to you. Refresh the lock or request control again.");
      return;
    }

    try {
      await axios.post("/api/FundingPackages/packages/UpdateFundingPackageStatus", {
        fundingPackageId: fundingPackage?.id,
        status: FundingPackageStatus.Funded
      });
      await fetchFundingPackages();
      navigate("/funding-packages");
    } catch (error) {
      setStatusUpdateError("This package is no longer locked to you. Refresh the lock or request control again.");
      await packageLock.refreshLock();
    }
  };
  const checkIfFundedReady = () => {
    return fundingPackage?.fundingAuthorizations?.some(
      (auth) => auth.authorizationStatus === AuthorizationStatus.Rejected
    );
  };
  useEffect(() => {
    getPackageInfo();
  }, [fundingPackage?.id]);

  useEffect(() => {
    if (!orderNumber) return;
    if (!rawPackageNumber) {
      navigate(`/review-funding-package/${orderNumber}-pkg1`, { replace: true });
    }
  }, [orderNumber, rawPackageNumber, navigate]);

  const [isCondensed, setIsCondensed] = useState<boolean>(false);
  return (
    <Stack position={"relative"} margin="0% auto" sx={{ display: "flex", justifyContent: "center", maxWidth: "90%" }}>
      <Stack margin="0% auto">
        <Typography variant="h4">Funding Package</Typography>
        <FundingPackageLockBanner
          lock={packageLock.lock}
          isLockedByMe={packageLock.isLockedByMe}
          isLockedByOtherUser={packageLock.isLockedByOtherUser}
          hasIncomingTakeoverRequest={packageLock.hasIncomingTakeoverRequest}
          takeoverRequestedByName={packageLock.takeoverRequestedByName}
          secondsUntilTakeoverAllowed={packageLock.secondsUntilTakeoverAllowed}
          onRequestTakeover={packageLock.requestTakeover}
          onAcceptTakeover={packageLock.acceptTakeover}
          onDeclineTakeover={packageLock.declineTakeover}
          onForceTakeover={packageLock.forceTakeover}
          onRelease={packageLock.releaseLock}
        />
        {statusUpdateError && <Alert severity="warning" sx={{ mb: 2 }}>{statusUpdateError}</Alert>}
        <Stack direction="row" spacing={2} justifyContent="space-between" marginTop={2}>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ maxWidth: 680 }}>
              <OrderDetails orderInfo={orderInfo} isCondensed={isCondensed} />
            </Stack>
            <NotesSection fundingPackageId={fundingPackage?.id} />
            <WiredIFrame orderNumber={orderNumber} />
          </Stack>
          <Stack direction="column" spacing={2} justifyContent="space-between">
            <Stack direction="row" flexWrap="wrap" gap={2} sx={{ maxWidth: 1000 }}>
              {fundingPackage?.fundingAuthorizations.map((authorization, index) => (
                <Box key={authorization.id || index}>
                  <ReviewUploadFunding
                    orderNumber={orderInfo?.number}
                    fundingAuthorization={authorization}
                    refreshData={getPackageInfo}
                    disabled={!packageLock.isLockedByMe}
                  />
                </Box>
              ))}
            </Stack>
            <Stack direction="row" justifyContent={"space-between"} spacing={2}>
              <Stack flex={1}>
                <ReviewFundingPackageDetails
                  fundingPackage={fundingPackage}
                  refreshData={getPackageInfo}
                  disabled={!packageLock.isLockedByMe}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 8 }}>
          <Button
            variant="outlined"
            sx={{
              color: "red",
              borderColor: "red",
              "&:hover": { borderColor: "red", backgroundColor: "#f5f5f5" }
            }}
            onClick={submitDeficient}
            disabled={!packageLock.isLockedByMe}
          >
            Deficient
          </Button>

          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: "purple",
              "&:hover": { backgroundColor: "#6a0dad" }
            }}
            onClick={submitFunded}
            disabled={checkIfFundedReady() || !packageLock.isLockedByMe}
          >
            Funded
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
